import { LaudoWizardData, Amostra, DadosComparativoDireto, TipoImovelAmostra, SignatureData } from '../types/laudo-wizard.types';
import { LaudoData, CalculoAvaliatario } from '../types';
import { InspectionRequest } from '@/services/types/inspection.types';
import { calcularEstatisticasCompletas } from './estatisticas-nbr14653';
import { AutomaticReportResponse, AutomaticReportData } from '@/services/aiAnalysis.service';
import { parseAndFixCurrencyValue } from './currencyUtils';
import { deriveFormatoLaudo } from './formatoLaudo';
import { mapPropertyTypeNameToTipoImovel } from './tipoImovelFallback';
import { getValorUnitarioConstrucaoAvaliando } from './calculoConstrucaoAvaliando';
import { parseLiquidez } from './liquidez';
import {
  calcularDepreciacao,
  calcularPorcentagemVidaUtil,
  getCoeficienteHeidecke,
} from '../components/LaudoWizard/steps/AmostrasStep/TabelaDepreciacao/utils';


export function transformLaudoWizardToLaudoData(
  wizardData: LaudoWizardData,
  inspection: InspectionRequest,
  questionarioData: any[]
): LaudoData {
  const firstItem = inspection.items?.[0];

  // Endereço editado na tela de Identificação tem precedência sobre o do item: o objeto
  // `inspection` não é recarregado depois do save, então ele continua com o valor antigo
  // durante a sessão. Sem edição, os campos do wizard vêm vazios e o item prevalece.
  const identificacao = wizardData.identificacao;

  const partesEndereco = {
    rua: identificacao.logradouro || firstItem?.street || '',
    numero: identificacao.numero || firstItem?.number || '',
    complemento: identificacao.complemento || firstItem?.complement || '',
    bairro: identificacao.bairro || firstItem?.district || '',
    cidade: identificacao.cidade || firstItem?.city || '',
    uf: identificacao.estado || firstItem?.state || '',
    cep: identificacao.cep || firstItem?.cep || '',
  };

  const valoresCalculados = calcularValoresFinais(wizardData);

  const fotoFachadaRef = extrairFotoFachada(wizardData, questionarioData);

  const laudoTransformado = {
    header: {
      empresaLogo: 'bari.',
      tituloDocumento: 'LAUDO DE AVALIAÇÃO',
      idLaudo: `#${inspection.proposalNumber || 'IAA' + Math.floor(Math.random() * 9999)}`,
      formatoLaudo: deriveFormatoLaudo(
        wizardData.avaliacao.dadosImovel.tipoImovel || firstItem?.propertyTypeName,
      ),
    },

    dadosPedido: {
      solicitanteNome: 'DI Documentação Imobiliária',
      propostaNumero: inspection.proposalNumber || 'N/A',
      dataSolicitacao: new Date(inspection.createdAt || Date.now()).toLocaleDateString('pt-BR'),
      empresaResponsavelNome: 'DI Documentação Imobiliária Ltda',
      avaliadorResponsavelNome: wizardData.signature.evaluatorName || 'N/A',
      avaliadorCrea: wizardData.signature.creaCauNumber || 'N/A',
      proponenteNome: (wizardData.identificacao.proponente && wizardData.identificacao.proponente !== 'N/A')
        ? wizardData.identificacao.proponente
        : (inspection.proponents?.[0]?.name || 'Não informado'),
      proponenteDocumento: inspection.proponents?.[0]?.cpfCnpj || 'Não informado',
    },

    dadosImovel: {
      enderecoRua: partesEndereco.rua,
      enderecoNumero: partesEndereco.numero,
      enderecoComplemento: partesEndereco.complemento,
      enderecoBairro: partesEndereco.bairro,
      enderecoMunicipio: partesEndereco.cidade,
      enderecoUf: partesEndereco.uf,
      enderecoCep: partesEndereco.cep,
      tipoImovel: wizardData.avaliacao.dadosImovel.tipoImovel || firstItem?.propertyTypeName || 'N/A',
      tipoImplantacao: wizardData.avaliacao.dadosImovel.tipoImplantacao || undefined,
      matriculaNumero: identificacao.matriculaNumero || firstItem?.registrationNumber || 'N/A',
      matriculaRegistroImoveis: identificacao.matriculaCartorio || firstItem?.registryOfficeNumber || 'N/A',
      iptuNumero: identificacao.iptuNumero || firstItem?.iptuNumber || 'N/A',
      // Exibição do bloco "Dados do Imóvel": vale o que foi informado na Identificação.
      // Os cálculos continuam usando `avaliacao.*` — ver TabelaAreaConstruida/CalculosAvaliatorios.
      areaConstruida: formatarArea(
        identificacao.areaConstruida || wizardData.avaliacao.dadosImovel.areaConstruida,
      ),
      areaTerreno: formatarArea(
        identificacao.areaTotal || wizardData.avaliacao.dadosTerreno?.areaTerreno,
      ),
      areaPrivativa: formatarArea(identificacao.areaPrivativa),
      testada: wizardData.avaliacao.dadosTerreno?.testada ? `${wizardData.avaliacao.dadosTerreno.testada}m` : undefined,
      topografia: wizardData.avaliacao.dadosTerreno?.topografia || undefined,
      padraoTerreno: wizardData.avaliacao.dadosTerreno?.padraoTerreno || undefined,
      areaComum: formatarArea(wizardData.avaliacao.dadosCondominio?.areaComum),
      estadoConservacaoCondominio: wizardData.avaliacao.dadosCondominio?.estadoConservacao || undefined,
    },

    resumoAvaliacao: {
      imagemDestaqueUrl: fotoFachadaRef.url || undefined,
      metodologiaAplicada: wizardData.amostras.metodologia || 'Comparativo direto de mercado',
      grauFundamentacao: wizardData.amostras.grauFundamentacao || 'Grau de Fundamentação II',
      grauPrecisao: wizardData.amostras.grauPrecisao || 'Grau de Precisão III',
      areaPrivativaTotal: formatarArea(wizardData.identificacao.areaPrivativa),
      valorUnitarioFinalM2: formatarMoeda(valoresCalculados.valorUnitarioFinal),
      valorFinalGarantia: formatarMoeda(valoresCalculados.valorFinalGarantia),
      valorSomenteAreaAverbada: (() => {
        if (valoresCalculados.valorTerreno || valoresCalculados.valorConstruida) {
          const valor = (valoresCalculados.valorTerreno || 0) + (valoresCalculados.valorConstruida || 0);
          return formatarMoeda(valor);
        }
        if (valoresCalculados.valorNaoAverbada && valoresCalculados.valorNaoAverbada > 0) {
          const valor = valoresCalculados.valorAvaliacaoCalculado - valoresCalculados.valorNaoAverbada;
          return formatarMoeda(valor);
        }
        return undefined;
      })(),
      valorAreaNaoAverbada: valoresCalculados.valorNaoAverbada && valoresCalculados.valorNaoAverbada > 0
        ? formatarMoeda(valoresCalculados.valorNaoAverbada)
        : undefined,
      valorComAreaNaoAverbada: valoresCalculados.valorNaoAverbada && valoresCalculados.valorNaoAverbada > 0
        ? formatarMoeda(valoresCalculados.valorAvaliacaoCalculado)
        : undefined,
      tipoImovel: wizardData.avaliacao.dadosImovel.tipoImovel,
      tipoImplantacao: wizardData.avaliacao.dadosImovel.tipoImplantacao,
      areaConstruida: formatarArea(wizardData.avaliacao.dadosImovel.areaConstruida),
      areaTerreno: formatarArea(wizardData.avaliacao.dadosTerreno?.areaTerreno),
      quantidadeQuartos: wizardData.avaliacao.dadosImovel.quantidadeQuartos,
      quantidadeBanheiros: wizardData.avaliacao.dadosImovel.quantidadeBanheiros,
      quantidadeVagas: wizardData.avaliacao.dadosImovel.quantidadeVagas,
      idadeAparente: wizardData.avaliacao.dadosImovel.idadeAparente,
      estadoConservacao: wizardData.avaliacao.dadosImovel.estadoConservacao,
      padraoAcabamento: wizardData.avaliacao.dadosImovel.padraoAcabamento,
      fatorLiquidez: parseLiquidez(wizardData.avaliacao.dadosImovel.fatorLiquidez),
    },

    vistoriaImovel: {
      questionario: questionarioData,
    },

    amostras: transformarAmostras(wizardData),

    avaliacaoMetodologia: {
      metodologia: wizardData.amostras.metodologia || 'Comparativo direto de mercado',
      grauPrecisao: wizardData.amostras.grauPrecisao || 'Grau de Precisão III',
      grauFundamentacao: wizardData.amostras.grauFundamentacao || 'Grau de Fundamentação II',
    },

    calculosAvaliatorios: transformarCalculosAvaliatorios(wizardData),

    tabelaDepreciacao: transformarTabelaDepreciacao(wizardData),
    tabelaAreaConstruida: transformarTabelaAreaConstruida(wizardData),
    tabelaValorTerreno: transformarTabelaValorTerreno(wizardData),

    homogeneizacao: calcularHomogeneizacao(wizardData),

    detalhamentoValores: {
      areaTerreno: formatarArea(valoresCalculados.areaTerreno),
      valorUnitarioTerreno: formatarMoeda(valoresCalculados.valorUnitarioTerreno),
      valorParcialTerreno: formatarMoeda(valoresCalculados.valorTerreno),
      areaConstruida: formatarArea(valoresCalculados.areaConstruida),
      valorUnitarioConstruida: formatarMoeda(valoresCalculados.valorUnitarioConstruida),
      valorParcialConstruida: formatarMoeda(valoresCalculados.valorConstruida),
      ...(valoresCalculados.areaNaoAverbadaM2 > 0 && {
        areaNaoAverbada: formatarArea(valoresCalculados.areaNaoAverbadaM2),
        valorUnitarioNaoAverbada: formatarMoeda(valoresCalculados.valorUnitarioNaoAverbada),
        valorParcialNaoAverbada: formatarMoeda(valoresCalculados.valorNaoAverbada),
      }),
      valorTotal: formatarMoeda(valoresCalculados.valorParcial),
    },

    quadroResumo: {
      valorAvaliacaoCalculado: formatarMoeda(valoresCalculados.valorAvaliacaoCalculado),
    },

    avaliacaoFinal: {
      // Exibição arredondada ao milhar mais próximo (cálculos internos seguem com o valor cheio)
      valorAvaliacao: formatarMoeda(Math.round(valoresCalculados.valorAvaliacaoCalculado / 1000) * 1000),
      valorAvaliacaoGarantia: formatarMoeda(valoresCalculados.valorFinalGarantia),
      valorGarantiaLeilao: formatarMoeda(Math.floor((valoresCalculados.valorFinalGarantia * 1.2) / 1000) * 1000),
      valorImovelLeilao: formatarMoeda(Math.floor((valoresCalculados.valorAvaliacaoCalculado * 1.2) / 1000) * 1000),
    },

    comentarios: wizardData.amostras.comentarios || '',

    relatorioFotografico: {
      listaFotos: extrairFotosDoQuestionario(questionarioData),
    },

    responsavelLaudo: {
      localData: formatarLocalData(),
      imagemAssinaturaCarimbo: wizardData.signature.signatureImage || '', // A imagem base64 da assinatura
      nomeResponsavel: wizardData.signature.evaluatorName || 'N/A',
      creaResponsavel: wizardData.signature.creaCauNumber || 'N/A',
    },

    fotoFachadaUrl: fotoFachadaRef.url,
    fotoFachadaCaption: wizardData.vistoria?.fotoFachadaCaption || undefined,
    // Sem foto anexada no wizard, referencia a foto do checklist escolhida como
    // fallback — quem transmite persiste esse id para a aprovação/Salesforce
    // exibirem a mesma imagem em vez de re-executar o fallback.
    fotoFachadaDocumentId: wizardData.vistoria?.fotoFachadaDocumentId || fotoFachadaRef.documentId,

    photoOrder: wizardData.vistoria?.photoOrder,

    footer: {
      versaoDocumento: '1.0.0',
      poweredBy: 'nextage.com',
      paginacao: '1 | 1',
    },
  };

  return laudoTransformado as LaudoData;
}

function isComparativoDireto(metodologia: string | undefined): boolean {
  if (!metodologia) {
    return false;
  }
  const metodologiaLower = metodologia.toLowerCase();
  const isComparativo = metodologiaLower.includes('comparativo') && metodologiaLower.includes('direto');
  return isComparativo;
}

function formatarArea(area: number | string | undefined): string {
  const areaNum = Number(area);
  if (!areaNum || isNaN(areaNum)) return '0,00m²';
  return `${areaNum.toFixed(2).replace('.', ',')}m²`;
}

function formatarMoeda(valor: number | string): string {
  const valorNum = Number(valor);
  if (!valorNum || isNaN(valorNum)) return 'R$ 0,00';
  return `R$ ${valorNum.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatarLocalData(): string {
  const agora = new Date();
  const diasSemana = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];
  const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

  const diaSemana = diasSemana[agora.getDay()];
  const dia = agora.getDate();
  const mes = meses[agora.getMonth()];
  const ano = agora.getFullYear();

  return `${diaSemana}, ${dia} de ${mes} de ${ano}`;
}

function transformarAmostras(wizardData: LaudoWizardData): any[] {
  const dadosComparativo = wizardData.amostras.dadosComparativoDireto || [];
  const dadosTerreno = wizardData.amostras.dadosValorTerreno || [];
  const dadosDepreciacao = wizardData.amostras.dadosDepreciacao || [];
  const dadosAreaConstruida = wizardData.amostras.dadosAreaConstruida || [];

  const amostrasCadastradas = wizardData.amostras.amostras || [];

  let metodologiaEfetiva = wizardData.amostras.metodologia;
  if (!metodologiaEfetiva) {
    if (dadosDepreciacao.length > 0 || dadosAreaConstruida.length > 0) {
      metodologiaEfetiva = 'Método Evolutivo';
    } else if (dadosComparativo.length > 0) {
      metodologiaEfetiva = 'Comparativo Direto de Mercado';
    } else {
      metodologiaEfetiva = 'Método Evolutivo';
    }
  }

  const usaComparativoDireto = isComparativoDireto(metodologiaEfetiva);

  // Helper para formatar endereço
  const formatarEndereco = (enderecoAmostra: any): string => {
    if (!enderecoAmostra) return 'Endereço não disponível';
    if (enderecoAmostra.bairro || enderecoAmostra.cidade) {
      return `${enderecoAmostra.rua}${enderecoAmostra.numero ? ', ' + enderecoAmostra.numero : ''} - ${enderecoAmostra.bairro}, ${enderecoAmostra.cidade}/${enderecoAmostra.uf}`
        .replace(/^, -/, '-')
        .replace(/ - ,/, ' -');
    }
    return enderecoAmostra.rua || 'Endereço não disponível';
  };

  // FONTE PRIMÁRIA: lista de amostras cadastradas (wizardData.amostras.amostras).
  // Antes, montávamos a partir das tabelas de cálculo (dadosComparativoDireto/
  // dadosValorTerreno), mas isso causava amostras vazias no PDF de forma intermitente
  // quando alguma sincronização entre amostras → tabelas falhava ou estava desatualizada.
  // Agora, sempre que houver amostras cadastradas, elas são a base, e os valores das
  // tabelas só ENRIQUECEM cada amostra. Mantemos os caminhos antigos como fallback
  // para compatibilidade com laudos legados que só tinham as tabelas populadas.
  const amostrasFiltradas = amostrasCadastradas.filter((a) => a.id !== 'Avaliando');

  if (amostrasFiltradas.length > 0) {
    return amostrasFiltradas.map((amostra) => {
      const dadosAmostra = amostra.dados;
      const enderecoAmostra = amostra.endereco;
      const depreciacaoItem = dadosDepreciacao.find((d) => d.identificacao === amostra.id);
      const comparativoItem = dadosComparativo.find((d) => d.amostra === amostra.id);
      const terrenoItem = dadosTerreno.find((d) => d.amostra === amostra.id);

      // Decide a fonte de área/valor unitário conforme a metodologia efetiva
      const areaPrivativa = usaComparativoDireto
        ? Number(comparativoItem?.area ?? dadosAmostra?.areaPrivativa ?? 0)
        : Number(terrenoItem?.areaTerreno ?? dadosAmostra?.areaTerreno ?? dadosAmostra?.areaPrivativa ?? 0);

      const valorAnuncio = usaComparativoDireto
        ? Number(comparativoItem?.valorAnuncio ?? terrenoItem?.valorTotalAmostra ?? dadosAmostra?.valorAnunciado ?? 0)
        : Number(terrenoItem?.valorTotalAmostra ?? comparativoItem?.valorAnuncio ?? dadosAmostra?.valorAnunciado ?? 0);

      const valorUnitario = usaComparativoDireto
        ? Number(comparativoItem?.valorM2 ?? dadosAmostra?.valorM2 ?? (areaPrivativa > 0 ? valorAnuncio / areaPrivativa : 0))
        : Number(terrenoItem?.valorUnitario ?? dadosAmostra?.valorM2 ?? (areaPrivativa > 0 ? valorAnuncio / areaPrivativa : 0));

      const imagemUrl = amostra.imagemUrl || dadosAmostra?.imagemUrl;

      return {
        idAmostra: amostra.id,
        externalId: amostra.externalId,
        valorTotalAmostra: formatarMoeda(valorAnuncio),
        endereco: formatarEndereco(enderecoAmostra),
        tipoImovel: dadosAmostra?.tipoImovel || depreciacaoItem?.tipoImovel || (usaComparativoDireto ? 'Casa' : 'N/A'),
        qtdQuartos: dadosAmostra?.quantidadeQuartos || 0,
        qtdBanheiros: dadosAmostra?.quantidadeBanheiros || 0,
        qtdVagas: dadosAmostra?.quantidadeVagas || 0,
        areaPrivativa,
        valorOfertaR$: formatarMoeda(valorAnuncio),
        valorUnitarioR$M2: formatarMoeda(valorUnitario),
        idadeAparente: depreciacaoItem?.idadeAparente || 0,
        padraoAcabamento: dadosAmostra?.padraoAcabamento || terrenoItem?.padrao || (usaComparativoDireto ? 'Normal' : 'N/A'),
        estadoConservacao: depreciacaoItem?.estadoConservacao || dadosAmostra?.estadoConservacao || 'N/A',
        urlFonte: dadosAmostra?.urlAnuncio || 'N/A',
        imagemUrl,
      };
    });
  }

  // FALLBACK: laudos legados sem `wizardData.amostras.amostras` populado,
  // que dependiam exclusivamente das tabelas de cálculo.
  let amostrasFinais: any[] = [];

  if (usaComparativoDireto && dadosComparativo.length > 0) {
    amostrasFinais = dadosComparativo
      .filter((d) => d.amostra !== 'Avaliando')
      .map((dado) => {
        const depreciacaoItem = dadosDepreciacao.find((d) => d.identificacao === dado.amostra);
        return {
          idAmostra: dado.amostra,
          valorTotalAmostra: formatarMoeda(Number(dado.valorAnuncio)),
          endereco: 'Endereço não disponível',
          tipoImovel: depreciacaoItem?.tipoImovel || 'Casa',
          qtdQuartos: 0,
          qtdBanheiros: 0,
          qtdVagas: 0,
          areaPrivativa: Number(dado.area),
          valorOfertaR$: formatarMoeda(Number(dado.valorAnuncio)),
          valorUnitarioR$M2: formatarMoeda(Number(dado.valorM2)),
          idadeAparente: depreciacaoItem?.idadeAparente || 0,
          padraoAcabamento: 'Normal',
          estadoConservacao: depreciacaoItem?.estadoConservacao || 'N/A',
          urlFonte: 'N/A',
        };
      });
  } else if (dadosTerreno.length > 0) {
    amostrasFinais = dadosTerreno
      .filter((d) => d.amostra !== 'Avaliando')
      .map((dado) => {
        const depreciacaoItem = dadosDepreciacao.find((d) => d.identificacao === dado.amostra);
        return {
          idAmostra: dado.amostra,
          valorTotalAmostra: formatarMoeda(Number(dado.valorTotalAmostra)),
          endereco: 'Endereço não disponível',
          tipoImovel: dado.tipoImovel || 'N/A',
          qtdQuartos: 0,
          qtdBanheiros: 0,
          qtdVagas: 0,
          areaPrivativa: Number(dado.areaTerreno),
          valorOfertaR$: formatarMoeda(Number(dado.valorTotalAmostra)),
          valorUnitarioR$M2: formatarMoeda(Number(dado.valorUnitario)),
          idadeAparente: depreciacaoItem?.idadeAparente || 0,
          padraoAcabamento: dado.padrao || 'N/A',
          estadoConservacao: depreciacaoItem?.estadoConservacao || 'N/A',
          urlFonte: 'N/A',
        };
      });
  }

  return amostrasFinais;
}

function transformarCalculosAvaliatorios(wizardData: LaudoWizardData): any {
  const usaComparativoDireto = isComparativoDireto(wizardData.amostras.metodologia);

  if (usaComparativoDireto) {
    const dadosComparativo = wizardData.amostras.dadosComparativoDireto;
    const parametrosFatoracao = wizardData.amostras.parametrosFatoracao || [];

    const fatorConfig: Record<string, { campo: string; nome: string }> = {
      regiao: { campo: 'fatorRegiao', nome: 'Região' },
      andar: { campo: 'fatorAndar', nome: 'Andar' },
      vaga: { campo: 'fatorVaga', nome: 'Vaga' },
      area: { campo: 'fatorArea', nome: 'Área' },
      padraoAcabamento: { campo: 'fatorPadraoAcabamento', nome: 'Padrão' },
      estadoConservacao: { campo: 'fatorEstadoConservacao', nome: 'Estado' },
      fatorExtraI: { campo: 'fatorExtraI', nome: 'Extra I' },
      fatorExtraII: { campo: 'fatorExtraII', nome: 'Extra II' },
      fatorExtraIII: { campo: 'fatorExtraIII', nome: 'Extra III' },
    };

    const fatoresAtivos: { key: string; campo: string; nome: string }[] = [
      { key: 'oferta', campo: 'fatorOferta', nome: 'Oferta' },
    ];

    parametrosFatoracao.forEach(param => {
      const config = fatorConfig[param];
      if (config) {
        fatoresAtivos.push({ key: param, campo: config.campo, nome: config.nome });
      }
    });

    const legendaFatores = fatoresAtivos
      .map((fator, idx) => `F${idx + 1} (${fator.nome})`)
      .join(', ');

    const tabelaCalculo = dadosComparativo
      .filter(d => d.amostra !== 'Avaliando') // Remove o item avaliando da tabela
      .map((dado, index) => {
        const media = dadosComparativo
          .filter(d => d.amostra !== 'Avaliando')
          .reduce((acc, d) => acc + d.valorHomogeneizado, 0) /
          dadosComparativo.filter(d => d.amostra !== 'Avaliando').length;

        const limiteInf = media * 0.7;
        const limiteSup = media * 1.3;
        const estaSaneado = dado.valorHomogeneizado >= limiteInf && dado.valorHomogeneizado <= limiteSup;

        const row: any = {
          id: index + 1,
          area: dado.area.toFixed(2).replace('.', ','),
          valorUnit: formatarMoeda(dado.valorM2),
        };

        fatoresAtivos.forEach((fator, idx) => {
          const valor = (dado as any)[fator.campo] || 1;
          row[`F${idx + 1}`] = valor.toFixed(2).replace('.', ',');
        });

        row.FAT = dado.fat.toFixed(3).replace('.', ',');
        row.SAN = estaSaneado ? 'S' : 'N';
        row.valorHomog = formatarMoeda(dado.valorHomogeneizado);

        return row;
      });

    const amostrasParaCalculo = dadosComparativo.filter(d => d.amostra !== 'Avaliando');
    const somaValoresOriginais = amostrasParaCalculo.reduce((acc, d) => acc + d.valorM2, 0);
    const somaValoresHomogeneizados = amostrasParaCalculo.reduce((acc, d) => acc + d.valorHomogeneizado, 0);
    const numAmostras = amostrasParaCalculo.length;

    return {
      legendaFatores,
      tabelaCalculo,
      fatoresAtivos: fatoresAtivos.length, // Número de fatores para renderização dinâmica
      mediaValorUnitarioOriginal: formatarMoeda(numAmostras > 0 ? somaValoresOriginais / numAmostras : 0),
      mediaValorHomogeneizado: formatarMoeda(numAmostras > 0 ? somaValoresHomogeneizados / numAmostras : 0),
    };
  }

  {
    const dadosAreaConstruida = wizardData.amostras.dadosAreaConstruida;
    const dadosDepreciacao = wizardData.amostras.dadosDepreciacao;

    const tabelaCalculo: CalculoAvaliatario[] = dadosAreaConstruida
      .filter(d => d.identificacao !== 'Avaliando')
      .map((dado, index) => {
        const depreciacao = dadosDepreciacao.find(dep => dep.identificacao === dado.identificacao);

        return {
          id: index + 1,
          area: dado.areaConstruida.toFixed(2).replace('.', ','),
          valorUnit: formatarMoeda(dado.cubBase),
          F1: dado.ajusteAcabamento.toFixed(2).replace('.', ','), // Ajuste Acabamento
          F2: depreciacao ? depreciacao.depreciacao.toFixed(3).replace('.', ',') : '1,000', // Depreciação
          F3: '-',
          F4: '-',
          F5: '-',
          F6: '-',
          F7: '-',
          F8: '-',
          F9: '-',
          FAT: (dado.ajusteAcabamento * (depreciacao?.depreciacao || 1)).toFixed(3).replace('.', ','),
          SAN: 'S',
          valorHomog: formatarMoeda(dado.valorFinal / dado.areaConstruida),
        };
      });

    return {
      legendaFatores: 'F1 (Ajuste Acabamento), F2 (Depreciacao Ross-Heidecke)',
      tabelaCalculo,
      fatoresAtivos: 2,
      mediaValorUnitarioOriginal: formatarMoeda(0), 
      mediaValorHomogeneizado: formatarMoeda(0),
    };
  }

  return {
    legendaFatores: '',
    tabelaCalculo: [],
    fatoresAtivos: 1,
    mediaValorUnitarioOriginal: 'R$ 0,00',
    mediaValorHomogeneizado: 'R$ 0,00',
  };
}

function calcularHomogeneizacao(wizardData: LaudoWizardData): any {
  const usaComparativoDireto = isComparativoDireto(wizardData.amostras.metodologia);
  const amostras = wizardData.amostras.amostras || [];

  let valoresHomogeneizados: number[] = [];

  if (usaComparativoDireto) {
    const dadosComparativo = wizardData.amostras.dadosComparativoDireto || [];
    // Filtra apenas amostras selecionadas para homogeneização (consistente com UI)
    const amostrasSelecionadas = amostras
      .filter(a => a.dados.selecionadaHomogeneizacao === true)
      .map(a => a.id);

    valoresHomogeneizados = dadosComparativo
      .filter(d => d.amostra !== 'Avaliando' && amostrasSelecionadas.includes(d.amostra))
      .map(d => d.valorHomogeneizado)
      .filter(v => v > 0);
  } else {
    const dadosTerreno = wizardData.amostras.dadosValorTerreno || [];
    valoresHomogeneizados = dadosTerreno
      .filter(d => d.amostra !== 'Avaliando')
      .map(d => d.valorUnitarioFinal)
      .filter(v => v > 0);
  }

  if (valoresHomogeneizados.length === 0) {
    return {
      nElementos: '0',
      valorUnitarioMedio: 'R$ 0,00',
      limiteInferior30: 'R$ 0,00',
      limiteSuperior30: 'R$ 0,00',
    };
  }

  const n = valoresHomogeneizados.length;
  const media = valoresHomogeneizados.reduce((acc, val) => acc + val, 0) / n;

  return {
    nElementos: n.toString(),
    valorUnitarioMedio: formatarMoeda(media),
    limiteInferior30: formatarMoeda(media * 0.7),
    limiteSuperior30: formatarMoeda(media * 1.3),
  };
}

// Espelhado em LaudoValoresCalculator (bari-salesforce-service), que devolve estes valores no
// /v1/imoveis/laudos. Mudou a regra aqui, mude lá também, senão a API diverge do PDF.
function calcularValoresFinais(wizardData: LaudoWizardData): any {
  const areaPrivativa = wizardData.identificacao.areaPrivativa || 0;

  const usaComparativoDireto = isComparativoDireto(wizardData.amostras.metodologia);

  let valorUnitarioFinal = 0;
  let valorParcial = 0;
  let areaParaDetalhamento = areaPrivativa;

  let areaTerreno = 0;
  let valorUnitarioTerreno = 0;
  let valorTerreno = 0;
  let areaConstruida = 0;
  let valorUnitarioConstruida = 0;
  let valorConstruida = 0;

  if (usaComparativoDireto) {
    const homog = calcularHomogeneizacao(wizardData);
    valorUnitarioFinal = parseFloat(
      homog.valorUnitarioMedio
        .replace('R$ ', '')
        .replace(/\./g, '')
        .replace(',', '.')
    );


    const areaConstruidaAvaliacao = wizardData.avaliacao?.dadosImovel?.areaConstruida ?? 0;
    const areaParaCalculo = areaConstruidaAvaliacao;

    valorParcial = valorUnitarioFinal * areaParaCalculo;
    areaConstruida = areaParaCalculo;
    valorUnitarioConstruida = valorUnitarioFinal;
    valorConstruida = valorParcial;
  }
  else {
    // Método Evolutivo
    const homog = calcularHomogeneizacao(wizardData);

    valorUnitarioFinal = parseFloat(
      homog.valorUnitarioMedio
        .replace('R$ ', '')
        .replace(/\./g, '')
        .replace(',', '.')
    );

    // Terreno: usar área do imóvel avaliando (do step de avaliação)
    // Fallback: tentar dadosValorTerreno com "Avaliando", ou identificacao
    const areaTerrAvaliacao = wizardData.avaliacao?.dadosTerreno?.areaTerreno || 0;
    const avaliandoTerreno = wizardData.amostras.dadosValorTerreno
      ?.find(d => d.amostra === 'Avaliando');
    const areaTerrFinal = areaTerrAvaliacao || avaliandoTerreno?.areaTerreno || 0;

    if (areaTerrFinal > 0) {
      areaParaDetalhamento = areaTerrFinal;
      areaTerreno = areaTerrFinal;
      valorUnitarioTerreno = valorUnitarioFinal;
      valorTerreno = valorUnitarioFinal * areaTerrFinal;
      valorParcial = valorTerreno;
    }

    const areaConstruidaAvaliacao = wizardData.avaliacao?.dadosImovel?.areaConstruida ?? 0;
    const cubAjustado = getValorUnitarioConstrucaoAvaliando(wizardData.amostras.dadosAreaConstruida);

    areaConstruida = areaConstruidaAvaliacao;
    valorUnitarioConstruida = cubAjustado;
    valorConstruida = cubAjustado * areaConstruidaAvaliacao; // 0 quando a averbada é 0
    valorParcial += valorConstruida;
  }

  let areaNaoAverbadaM2 = 0;
  let valorUnitarioNaoAverbada = 0;
  let valorNaoAverbada = 0;
  if (wizardData.amostras.especificarAreaNaoAverbada) {
    areaNaoAverbadaM2 = wizardData.amostras.areaNaoAverbada || 0;
    const fatorAjuste = wizardData.amostras.fatorAjusteNaoAverbada || 1;

    // Espelha useValorNaoAverbada (UI): a base é o mesmo valor unitário da
    // Área Construída averbada — homogeneizado em CD, cubAjustado em Evolutivo.
    valorUnitarioNaoAverbada = valorUnitarioConstruida * fatorAjuste;
    valorNaoAverbada = valorUnitarioNaoAverbada * areaNaoAverbadaM2;
    valorParcial += valorNaoAverbada;
  }

  const valorAvaliacaoCalculado = valorParcial;
  const valorParaEfeitoGarantia = valorTerreno + valorConstruida;
  const valorFinalGarantia = Math.round(valorParaEfeitoGarantia / 1000) * 1000; // Arredonda ao milhar mais próximo (>= 500 sobe)

  return {
    valorUnitarioFinal,
    valorParcial,
    valorAvaliacaoCalculado,
    valorFinalGarantia,
    areaParaDetalhamento,
    areaTerreno,
    valorUnitarioTerreno,
    valorTerreno,
    areaConstruida,
    valorUnitarioConstruida,
    valorConstruida,
    areaNaoAverbadaM2,
    valorUnitarioNaoAverbada,
    valorNaoAverbada,
  };
}

function extrairFotosDoQuestionario(questionarioData: any[]): any[] {
  const fotos: any[] = [];

  questionarioData.forEach(item => {
    if (item.answerType === 'Photo' && item.photos && item.photos.length > 0 && item.showInSimplifiedReport !== false) {
      item.photos.forEach((photo: any) => {
        const foto = {
          imagemUrl: photo.fileUrl,
          legenda: photo.caption || item.pergunta,
          documentId: photo.fileHash || photo.documentId, // fileHash é usado como documentId para renovação de URL
        };

        fotos.push(foto);
      });
    }
  });

  return fotos;
}

// Foto de fachada exibida no laudo: a anexada no wizard ou, na ausência dela, a
// primeira foto do checklist cuja pergunta indique fachada/frontal. Retorna
// também o documentId da foto escolhida — re-executar este fallback depois (ex.:
// na aprovação) pode escolher outra foto se o checklist tiver mudado, então o
// chamador deve persistir a referência na transmissão.
function extrairFotoFachada(
  wizardData: LaudoWizardData,
  questionarioData: any[]
): { url: string | null; documentId?: string } {
  if (wizardData.vistoria?.fotoFachada) {
    return {
      url: wizardData.vistoria.fotoFachada,
      documentId: wizardData.vistoria.fotoFachadaDocumentId,
    };
  }

  for (const item of questionarioData) {
    if (item.answerType === 'Photo' && item.photos && item.photos.length > 0) {
      const perguntaLowerCase = item.pergunta?.toLowerCase() || '';
      if (perguntaLowerCase.includes('fachada') || perguntaLowerCase.includes('frontal')) {
        const photo = item.photos[0];
        return {
          // fileHash é usado como documentId para renovação de URL (vide extrairFotosDoQuestionario)
          url: photo.fileUrl,
          documentId: photo.fileHash || photo.documentId,
        };
      }
    }
  }

  return { url: null };
}


// Numeração única das amostras selecionadas para o PDF — mesma regra do
// displayIdMap da tela de edição dos cálculos (#1..#N na ordem escolhida,
// ranking; fallback: ordem de cadastro), para que a numeração e a ordem das
// tabelas do laudo batam com o que o analista vê na tela.
function construirNumeracaoAmostrasPdf(wizardData: LaudoWizardData): {
  numeroPorId: Map<string, string>;
  ordemPorId: Map<string, number>;
} {
  const amostras = wizardData.amostras.amostras || [];
  const selecionadas = amostras.filter((a) => a.dados?.selecionadaHomogeneizacao === true);
  const ordenadas = [...selecionadas].sort(
    (a, b) =>
      (a.dados?.rankingHomogeneizacao ?? Number.MAX_SAFE_INTEGER) -
      (b.dados?.rankingHomogeneizacao ?? Number.MAX_SAFE_INTEGER)
  );

  const numeroPorId = new Map<string, string>();
  const ordemPorId = new Map<string, number>([['Avaliando', -1]]);
  ordenadas.forEach((amostra, idx) => {
    numeroPorId.set(amostra.id, `#${idx + 1}`);
    ordemPorId.set(amostra.id, idx);
  });

  return { numeroPorId, ordemPorId };
}

function ordenarPorNumeracao<T>(
  itens: T[],
  getId: (item: T) => string,
  ordemPorId: Map<string, number>
): T[] {
  return [...itens].sort(
    (a, b) =>
      (ordemPorId.get(getId(a)) ?? Number.MAX_SAFE_INTEGER) -
      (ordemPorId.get(getId(b)) ?? Number.MAX_SAFE_INTEGER)
  );
}

function transformarTabelaDepreciacao(wizardData: LaudoWizardData): any[] | undefined {
  const usaComparativoDireto = isComparativoDireto(wizardData.amostras.metodologia);
  const usaMetodoEvolutivo = !usaComparativoDireto;

  if (!usaMetodoEvolutivo) {
    return undefined;
  }

  const dadosDepreciacao = wizardData.amostras.dadosDepreciacao || [];

  if (dadosDepreciacao.length === 0) {
    return undefined;
  }

  // Filtra por amostras selecionadas para homogeneização
  const amostras = wizardData.amostras.amostras || [];
  const idsSelecionadas = new Set(
    amostras.filter(a => a.dados.selecionadaHomogeneizacao === true).map(a => a.id)
  );

  const dadosFiltrados = dadosDepreciacao.filter(item =>
    item.tipoImovel !== 'Terreno' &&
    (item.identificacao === 'Avaliando' || idsSelecionadas.has(item.identificacao))
  );

  // Para a linha "Avaliando", a fonte de verdade são os dados editados pelo analista
  // em wizardData.avaliacao.dadosImovel. O dadosDepreciacao[0] pode estar dessincronizado
  // se o analista editou Avaliação sem visitar o Step Amostras (onde roda o useEffect de sync).
  const dadosImovel = wizardData.avaliacao?.dadosImovel;
  const idadeAvaliando = Number(dadosImovel?.idadeAparente) || 0;
  const estadoAvaliando = dadosImovel?.estadoConservacao || 'Bom';

  // Numeração e ordem únicas, compartilhadas com a tela de edição dos cálculos
  const { numeroPorId, ordemPorId } = construirNumeracaoAmostrasPdf(wizardData);
  const dadosOrdenados = ordenarPorNumeracao(dadosFiltrados, (item) => item.identificacao, ordemPorId);

  return dadosOrdenados.map((item) => {
    const isAvaliando = item.identificacao === 'Avaliando';
    const idadeAparente = isAvaliando ? idadeAvaliando : item.idadeAparente;
    const estadoConservacao = isAvaliando ? estadoAvaliando : item.estadoConservacao;

    // Recalcula coefHeidecke/porcentagemVU/depreciacao para o Avaliando caso
    // os valores tenham sido sobrescritos com os do step Avaliação.
    const coefHeidecke = isAvaliando ? getCoeficienteHeidecke(estadoConservacao) : item.coefHeidecke;
    const porcentagemVU = isAvaliando ? calcularPorcentagemVidaUtil(idadeAparente, item.vidaUtil) : item.porcentagemVU;
    const depreciacao = isAvaliando
      ? calcularDepreciacao(idadeAparente, item.vidaUtil, coefHeidecke, item.valorResidual ?? 0.2)
      : item.depreciacao;

    return {
      identificacao: isAvaliando ? 'Avaliando' : (numeroPorId.get(item.identificacao) || item.identificacao),
      tipoImovel: item.tipoImovel,
      idadeAparente: idadeAparente.toString(),
      estadoConservacao,
      vidaUtil: item.vidaUtil.toString(),
      porcentagemVU: porcentagemVU.toFixed(2) + '%',
      coefHeidecke: coefHeidecke.toFixed(3),
      depreciacao: depreciacao.toFixed(3),
    };
  });
}

function transformarTabelaAreaConstruida(wizardData: LaudoWizardData): any[] | undefined {
  const usaComparativoDireto = isComparativoDireto(wizardData.amostras.metodologia);
  const usaMetodoEvolutivo = !usaComparativoDireto;

  if (!usaMetodoEvolutivo) {
    return undefined;
  }

  const dadosAreaConstruida = wizardData.amostras.dadosAreaConstruida || [];

  if (dadosAreaConstruida.length === 0) {
    return undefined;
  }

  // Filtra por amostras selecionadas para homogeneização
  const amostras = wizardData.amostras.amostras || [];
  const idsSelecionadas = new Set(
    amostras.filter(a => a.dados.selecionadaHomogeneizacao === true).map(a => a.id)
  );

  const dadosFiltrados = dadosAreaConstruida.filter(item =>
    item.identificacao === 'Avaliando' || idsSelecionadas.has(item.identificacao)
  );

  // Numeração e ordem únicas, compartilhadas com a tela de edição dos cálculos
  const { numeroPorId, ordemPorId } = construirNumeracaoAmostrasPdf(wizardData);
  const dadosOrdenados = ordenarPorNumeracao(dadosFiltrados, (item) => item.identificacao, ordemPorId);

  return dadosOrdenados.map((item) => ({
    identificacao: item.identificacao === 'Avaliando'
      ? 'Avaliando'
      : (numeroPorId.get(item.identificacao) || item.identificacao),
    tipoImovel: item.tipoImovel,
    areaConstruida: formatarArea(item.areaConstruida),
    cubBase: item.tipoImovel === 'Apartamento' ? '—' : formatarMoeda(item.cubBase),
    ajusteAcabamento: item.tipoImovel === 'Apartamento' ? '—' : item.ajusteAcabamento.toFixed(2),
    depreciacao: item.depreciacao.toFixed(3),
    cubAjustado: formatarMoeda(item.cubAjustado),
    valorFinal: formatarMoeda(item.valorFinal),
  }));
}

function transformarTabelaValorTerreno(wizardData: LaudoWizardData): any[] | undefined {
  const usaComparativoDireto = isComparativoDireto(wizardData.amostras.metodologia);
  const usaMetodoEvolutivo = !usaComparativoDireto;

  if (!usaMetodoEvolutivo) {
    return undefined;
  }

  const dadosValorTerreno = wizardData.amostras.dadosValorTerreno || [];

  // Filtra por amostras selecionadas para homogeneização
  const amostras = wizardData.amostras.amostras || [];
  const idsSelecionadas = new Set(
    amostras.filter(a => a.dados.selecionadaHomogeneizacao === true).map(a => a.id)
  );

  const dadosFiltrados = dadosValorTerreno.filter(
    item => item.amostra !== 'Avaliando' && item.tipoImovel !== 'Apartamento' &&
    idsSelecionadas.has(item.amostra)
  );

  if (dadosFiltrados.length === 0) {
    return undefined;
  }

  const switches = wizardData.amostras.switchesTerreno || {};

  // Numeração e ordem únicas, compartilhadas com a tela de edição dos cálculos
  const { numeroPorId, ordemPorId } = construirNumeracaoAmostrasPdf(wizardData);
  const dadosOrdenados = ordenarPorNumeracao(dadosFiltrados, (item) => item.amostra, ordemPorId);

  return dadosOrdenados.map((item) => {
    const baseItem: any = {
      amostra: numeroPorId.get(item.amostra) || item.amostra,
      tipoImovel: item.tipoImovel,
      areaTerreno: formatarArea(item.areaTerreno),
      padrao: item.padrao,
      topografia: item.topografia,
      valorTerreno: formatarMoeda(item.valorTerreno),
      valorUnitario: formatarMoeda(item.valorUnitario),
      indiceFiscal: item.indiceFiscal.toFixed(3),
      fatorOferta: item.fatorOferta.toFixed(3),
      fatorTopografia: item.fatorTopografia.toFixed(3),
      fat: item.fat.toFixed(3),
      valorUnitarioFinal: formatarMoeda(item.valorUnitarioFinal),
    };

    if (switches.area) {
      baseItem.fatorArea = item.fatorArea.toFixed(3);
    }
    if (switches.testada) {
      baseItem.fatorTestada = item.fatorTestada.toFixed(3);
    }
    if (switches.vegetacao) {
      baseItem.fatorVegetacao = item.fatorVegetacao.toFixed(3);
    }
    if (switches.fatorExtraI) {
      baseItem.fatorExtraI = item.fatorExtraI.toFixed(3);
    }
    if (switches.fatorExtraII) {
      baseItem.fatorExtraII = item.fatorExtraII.toFixed(3);
    }
    if (switches.fatorExtraIII) {
      baseItem.fatorExtraIII = item.fatorExtraIII.toFixed(3);
    }

    return baseItem;
  });
}

export interface LoggedUserSignature {
  evaluatorName: string;
  creaCauNumber?: string;
  signatureImage?: string;
}

export function transformAiReportToWizardData(
  aiReport: AutomaticReportResponse,
  inspection: InspectionRequest,
  loggedUserSignature?: LoggedUserSignature,
  fotoFachadaBase64?: string
): LaudoWizardData {
  const reportData = aiReport.reportData;
  const firstItem = inspection.items?.[0];

  if (!reportData) {
    throw new Error('Dados do laudo automático não disponíveis');
  }

  // Determina metodologia pelo tipo de imóvel (mesma lógica de transformAiReportToLaudoData).
  // Fallback: tipo cadastrado na inspeção — um default fixo "Apartamento" trocava a
  // metodologia (e zerava o memorial) de vistorias de casa/terreno.
  const tipoAvaliando = reportData.dadosImovel.tipoImovel
    || mapPropertyTypeNameToTipoImovel(firstItem?.propertyTypeName);
  const TIPOS_EVOLUTIVO = ['casa', 'sobrado', 'terreno', 'lote', 'chácara', 'fazenda', 'sítio'];
  const tipoImovelAi = tipoAvaliando.toLowerCase().trim();
  const usarComparativoDireto = !TIPOS_EVOLUTIVO.some(t => tipoImovelAi.includes(t));
  const nomeMetodologia = usarComparativoDireto
    ? 'Comparativo Direto de Mercado'
    : 'Método Evolutivo';

  // Usa utilitário compartilhado para parsing de valores monetários
  const parseValor = parseAndFixCurrencyValue;

  const parseArea = (areaStr: string): number => {
    if (!areaStr) return 0;
    return parseFloat(areaStr.replace('m²', '').replace(',', '.').trim()) || 0;
  };

  const amostras: Amostra[] = (reportData.amostras || []).map((amostra, index) => ({
    id: `#${index + 1}`,
    externalId: amostra.idAmostra || undefined,
    endereco: {
      rua: amostra.endereco || '',
      numero: '',
      complemento: '',
      bairro: '',
      cidade: '',
      uf: '',
      cep: '',
    },
    dados: {
      tipoImovel: amostra.tipoImovel || 'Apartamento',
      areaPrivativa: amostra.areaPrivativa || 0,
      areaTerreno: amostra.areaTerreno || 0,
      quantidadeQuartos: amostra.quartos || 0,
      quantidadeBanheiros: amostra.banheiros || 0,
      quantidadeVagas: amostra.vagas || 0,
      padraoAcabamento: amostra.padraoAcabamento || 'Normal',
      estadoConservacao: amostra.estadoConservacao || 'Bom',
      valorAnunciado: parseValor(amostra.valorOferta, false),
      valorM2: parseValor(amostra.valorUnitarioM2, true),
      fonteAnuncio: 'IA Automático',
      urlAnuncio: amostra.urlFonte || '',
      imagemUrl: amostra.imagemUrl || '',
      dataAnuncio: new Date().toISOString(),
      selecionadaHomogeneizacao: true,
      rankingHomogeneizacao: amostra.ranking,
    },
    imagemUrl: amostra.imagemUrl || undefined,
    coordenadas: undefined,
  }));

  const dadosComparativoDireto: DadosComparativoDireto[] = [
    {
      amostra: 'Avaliando',
      area: parseArea(reportData.dadosImovel.areaPrivativa),
      valorAnuncio: 0,
      valorM2: 0,
      fatorOferta: 1,
      fatorRegiao: 1,
      fatorAndar: 1,
      fatorVaga: 1,
      fatorArea: 1,
      fatorPadraoAcabamento: 1,
      fatorEstadoConservacao: 1,
      fatorExtraI: 1,
      fatorExtraII: 1,
      fatorExtraIII: 1,
      fat: 1,
      valorHomogeneizado: 0,
    },
    ...amostras.map((amostra, index) => ({
      amostra: `#${index + 1}`,
      area: amostra.dados.areaPrivativa,
      valorAnuncio: amostra.dados.valorAnunciado,
      valorM2: amostra.dados.valorM2,
      fatorOferta: 0.9, 
      fatorRegiao: 1,
      fatorAndar: 1,
      fatorVaga: 1,
      fatorArea: 1,
      fatorPadraoAcabamento: 1,
      fatorEstadoConservacao: 1,
      fatorExtraI: 1,
      fatorExtraII: 1,
      fatorExtraIII: 1,
      fat: 0.9,
      valorHomogeneizado: amostra.dados.valorM2 * 0.9,
    })),
  ];


  const dadosDepreciacao = [
    {
      identificacao: 'Avaliando',
      tipoImovel: tipoAvaliando as TipoImovelAmostra,
      idadeAparente: 0,
      estadoConservacao: 'Bom',
      vidaUtil: 60,
      valorResidual: 0.2,
      coefHeidecke: 1,
      porcentagemVU: 0,
      depreciacao: 1,
    },
    ...amostras.map((amostra, index) => ({
      identificacao: `#${index + 1}`,
      tipoImovel: (amostra.dados.tipoImovel || 'Apartamento') as TipoImovelAmostra,
      idadeAparente: 0,
      estadoConservacao: amostra.dados.estadoConservacao || 'Bom',
      vidaUtil: 60,
      valorResidual: 0.2,
      coefHeidecke: 1,
      porcentagemVU: 0,
      depreciacao: 1,
      selecionadaHomogeneizacao: true,
      areaPrivativa: amostra.dados.areaPrivativa,
      areaTerreno: amostra.dados.areaTerreno || 0,
      valorTotal: amostra.dados.valorAnunciado,
    })),
  ];

  // Cria dadosAreaConstruida com o valorM2 como cubBase para preservar os valores do laudo automático
  const dadosAreaConstruida = [
    {
      identificacao: 'Avaliando',
      tipoImovel: tipoAvaliando as TipoImovelAmostra,
      areaConstruida: Math.round(parseArea(reportData.dadosImovel.areaConstruida || reportData.dadosImovel.areaPrivativa) * 100) / 100,
      cubBase: Math.round(parseValor(reportData.avaliacaoFinal.valorM2Medio, true) * 100) / 100, // Usa o valor M2 médio como CUB base
      ajusteAcabamento: 1.0,
      depreciacao: 0,
      cubAjustado: Math.round(parseValor(reportData.avaliacaoFinal.valorM2Medio, true) * 100) / 100,
      valorFinal: Math.round(parseValor(reportData.avaliacaoFinal.valorAvaliacao, false) * 100) / 100,
    },
    ...amostras.map((amostra, index) => ({
      identificacao: `#${index + 1}`,
      tipoImovel: (amostra.dados.tipoImovel || 'Apartamento') as TipoImovelAmostra,
      areaConstruida: Math.round(amostra.dados.areaPrivativa * 100) / 100,
      cubBase: Math.round(amostra.dados.valorM2 * 100) / 100, // Usa o valorM2 da amostra como CUB base (2 casas decimais)
      ajusteAcabamento: 1.0,
      depreciacao: 0,
      cubAjustado: Math.round(amostra.dados.valorM2 * 100) / 100,
      valorFinal: Math.round(amostra.dados.valorAnunciado * 100) / 100,
    })),
  ];
 

  const wizardData: LaudoWizardData = {
    checklist: {
      checklistId: '',
      checklistPreenchido: true, 
      respostas: [],
    },

    identificacao: {
      proponente: reportData.dadosPedido.proponenteNome || inspection.proponents?.[0]?.name || 'N/A',
      enderecoCompleto: `${reportData.dadosImovel.enderecoRua}, ${reportData.dadosImovel.enderecoNumero} - ${reportData.dadosImovel.enderecoBairro}, ${reportData.dadosImovel.enderecoMunicipio}/${reportData.dadosImovel.enderecoUf}`,
      cep: reportData.dadosImovel.enderecoCep || firstItem?.cep || '',
      logradouro: reportData.dadosImovel.enderecoRua || firstItem?.street || '',
      numero: reportData.dadosImovel.enderecoNumero || firstItem?.number || '',
      complemento: reportData.dadosImovel.enderecoComplemento || firstItem?.complement || '',
      bairro: reportData.dadosImovel.enderecoBairro || firstItem?.district || '',
      cidade: reportData.dadosImovel.enderecoMunicipio || firstItem?.city || '',
      estado: reportData.dadosImovel.enderecoUf || firstItem?.state || '',
      matriculaNumero: reportData.dadosImovel.matriculaNumero || firstItem?.registrationNumber || '',
      matriculaCartorio: reportData.dadosImovel.matriculaRegistroImoveis || firstItem?.registryOfficeNumber || '',
      areaTotal: parseArea(reportData.dadosImovel.areaTerreno),
      areaPrivativa: parseArea(reportData.dadosImovel.areaPrivativa),
      tipoSolicitacao: 'Avaliação de Garantia',
    },

    vistoria: {
      checklistId: '',
      checklistRespostas: {},
      processedResponses: [],
      fotoFachada: fotoFachadaBase64,
      dataVistoria: new Date().toLocaleDateString('pt-BR'),
      vistoriador: 'Análise Automática (IA)',
    },

    avaliacao: {
      dadosImovel: {
        tipoImovel: tipoAvaliando,
        tipoImplantacao: 'Isolado',
        areaConstruida: parseArea(reportData.dadosImovel.areaConstruida),
        quantidadeQuartos: 0,
        quantidadeBanheiros: 0,
        quantidadeVagas: 0,
        idadeAparente: 0,
        estadoConservacao: 'Bom',
        padraoAcabamento: 'Normal',
      },
      dadosTerreno: {
        areaTerreno: parseArea(reportData.dadosImovel.areaTerreno),
        testada: 0,
        padraoTerreno: 'Urbano',
        topografia: 'Plano',
      },
      dadosCondominio: {
        areaComum: 0,
        estadoConservacao: 'Bom',
      },
      _initialized: true,
    },

    amostras: {
      amostras: amostras,
      metodologiaImportacao: 'planilha',
      metodologia: nomeMetodologia,
      parametrosFatoracao: ['oferta'],
      dadosAreaConstruida: dadosAreaConstruida,
      dadosDepreciacao: dadosDepreciacao,
      dadosValorTerreno: [],
      switchesTerreno: {
        area: false,
        testada: false,
        vegetacao: false,
        fatorExtraI: false,
        fatorExtraII: false,
        fatorExtraIII: false,
      },
      dadosComparativoDireto: dadosComparativoDireto,
      grauPrecisao: reportData.resumoAvaliacao.grauPrecisao || 'Grau de Precisão III',
      grauFundamentacao: reportData.resumoAvaliacao.grauFundamentacao || 'Grau de Fundamentação II',
      especificarAreaNaoAverbada: false,
      areaNaoAverbada: 0,
      fatorAjusteNaoAverbada: 1,
      comentarios: reportData.observacoes || 'Laudo gerado automaticamente por Inteligência Artificial.',
    },

    signature: {
      evaluatorName: loggedUserSignature?.evaluatorName || '',
      creaCauNumber: loggedUserSignature?.creaCauNumber || '',
      signatureImage: loggedUserSignature?.signatureImage || '',
    },

    transmissao: {
      transmissao: {
        sistemaDestino: 'Banco Bari',
        statusTransmissao: 'pendente',
      },
      pdfGerado: false,
      processingUrls: false,
    },
  };

  return wizardData;
}
